﻿import { useCallback, useEffect, useState } from 'react';
import { Alert, Input, Modal, Spin, Tag } from 'antd';
import { mFetch, MApiError } from '../mApi';
import { MPageTitle } from '../MobileApp';

interface Lead {
  id: number | string;
  companyName?: string | null;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  leadStatus?: string | null;
  source?: string | null;
  remark?: string | null;
  createdAt?: string;
}

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  new: { text: '新线索', color: 'blue' },
  contacted: { text: '已联系', color: 'gold' },
  converted: { text: '已转化', color: 'green' },
  invalid: { text: '无效', color: 'default' },
  lost: { text: '已流失', color: 'red' },
};

export default function MLeads() {
  const [items, setItems] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [kw, setKw] = useState('');
  const [current, setCurrent] = useState<Lead | null>(null);

  const load = useCallback(async (keyword?: string) => {
    setLoading(true);
    setErr('');
    try {
      const r = await mFetch<{ items: Lead[]; total: number }>(
        `/admin/leads?page=1&pageSize=30${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ''}`,
      );
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      setErr(e instanceof MApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <MPageTitle title={`留言（${total}）`} />
      <div style={{ padding: '0 16px 8px' }}>
        <Input.Search
          data-testid="m-leads-search"
          placeholder="搜索公司 / 联系人 / 电话"
          allowClear
          onSearch={(v) => load(v.trim() || undefined)}
          onChange={(e) => setKw(e.target.value)}
          value={kw}
        />
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : err ? (
        <div style={{ padding: 16 }}><Alert type="warning" showIcon title={err} /></div>
      ) : (
        <div style={{ padding: '0 16px' }}>
          {items.length === 0 && <div style={{ color: '#8a8aa3', padding: 20, textAlign: 'center' }}>暂无留言</div>}
          {items.map((l) => {
            const st = STATUS_LABEL[String(l.leadStatus || 'new')] || { text: String(l.leadStatus || '-'), color: 'default' };
            return (
              <div
                key={String(l.id)}
                data-testid="m-lead-item"
                onClick={() => setCurrent(l)}
                style={{ background: '#141428', border: '1px solid #26264a', borderRadius: 10, padding: 12, marginBottom: 8, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>{l.companyName || l.contactName || '未署名'}</span>
                  <Tag color={st.color}>{st.text}</Tag>
                </div>
                <div style={{ fontSize: 13, color: '#8a8aa3', marginTop: 4 }}>
                  {l.contactName || '-'} · {l.phone || '-'}
                </div>
                <div style={{ fontSize: 12, color: '#5c5c75', marginTop: 2 }}>
                  {l.createdAt ? new Date(l.createdAt).toLocaleString('zh-CN') : ''} {l.source ? `· ${l.source}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal
        open={!!current}
        title="留言详情"
        footer={null}
        onCancel={() => setCurrent(null)}
        styles={{ body: { background: '#141428' } }}
      >
        {current && (
          <div style={{ fontSize: 14, lineHeight: 2 }}>
            <div>公司：{current.companyName || '-'}</div>
            <div>联系人：{current.contactName || '-'}</div>
            <div>
              电话：{current.phone ? <a href={`tel:${current.phone}`}>{current.phone}</a> : '-'}
            </div>
            <div>邮箱：{current.email || '-'}</div>
            <div>来源：{current.source || '-'}</div>
            <div>备注：{current.remark || '-'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
}
